/**
 * PortfolioKpiStrip — five horizontal cards across the top of the home
 * dashboard with "now · WoW delta" for the five portfolio-wide metrics.
 */
import React from "react";
import { ArrowUp, ArrowDown, Minus, Hash, Trophy, Target, PoundSterling, AlertTriangle } from "lucide-react";

function fmtInt(n) {
  if (n == null) return "—";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "K";
  return String(Math.round(n));
}
function fmtMoney(n) {
  if (n == null || n === 0) return "£0";
  if (n >= 1_000_000) return "£" + (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return "£" + (n / 1_000).toFixed(1) + "K";
  return "£" + Math.round(n).toLocaleString();
}

function DeltaBadge({ delta, pct, invert = false }) {
  if (delta == null || delta === 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-[10px] text-[#718096] font-mono-tight">
        <Minus className="h-2.5 w-2.5" strokeWidth={3} /> flat
      </span>
    );
  }
  // invert=true means "lower delta is good" (e.g. at-risk count going down).
  const up = delta > 0;
  const good = invert ? !up : up;
  const Icon = up ? ArrowUp : ArrowDown;
  const colour = good ? "text-[#2c7a7b]" : "text-[#c05621]";
  const abs = Math.abs(delta);
  const label = pct != null ? `${abs >= 1 ? fmtInt(abs) : abs} · ${pct > 0 ? "+" : ""}${pct.toFixed(1)}%` : fmtInt(abs);
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-mono-tight font-semibold ${colour}`}>
      <Icon className="h-2.5 w-2.5" strokeWidth={3} /> {label}
    </span>
  );
}

function Kpi({ icon: Icon, label, value, delta, invert, testid }) {
  return (
    <div className="card-soft p-4 min-w-0" data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold truncate">{label}</div>
        <div className="h-6 w-6 rounded-md bg-[#4fd1c5]/10 flex items-center justify-center flex-none">
          <Icon className="h-3 w-3 text-[#2c7a7b]" strokeWidth={2.4} />
        </div>
      </div>
      <div className="font-display text-2xl text-[#1a202c] mt-1 tabular-nums">{value}</div>
      <div className="mt-1">
        <DeltaBadge delta={delta?.delta} pct={delta?.delta_pct} invert={invert} />
        <span className="text-[10px] text-[#a0aec0] ml-1.5">vs last week</span>
      </div>
    </div>
  );
}

export default function PortfolioKpiStrip({ data, loading }) {
  if (loading || !data) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="portfolio-kpi-strip-loading">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="card-soft p-4 animate-pulse">
            <div className="h-3 w-20 bg-[#edf2f7] rounded mb-3" />
            <div className="h-7 w-16 bg-[#edf2f7] rounded" />
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="portfolio-kpi-strip">
      <Kpi icon={Hash} label="Tracked keywords" value={fmtInt(data.tracked_keywords?.now)} delta={data.tracked_keywords} testid="kpi-tracked" />
      <Kpi icon={Trophy} label="In top 3" value={fmtInt(data.top3?.now)} delta={data.top3} testid="kpi-top3" />
      <Kpi icon={Target} label="In top 10" value={fmtInt(data.top10?.now)} delta={data.top10} testid="kpi-top10" />
      <Kpi icon={PoundSterling} label="Traffic value / mo" value={fmtMoney(data.traffic_value_monthly?.now)} delta={data.traffic_value_monthly} testid="kpi-tv" />
      <Kpi icon={AlertTriangle} label="At-risk clients" value={fmtInt(data.at_risk?.count || 0)} delta={null} invert testid="kpi-at-risk" />
    </div>
  );
}
